import { useState, type FormEvent } from 'react';
import { Lock } from 'lucide-react';
import { RU_BANKS } from '@shared/types';

import { Button } from '@/components/ui/Button';
import { SelectField, TextField } from '@/components/ui/Field';
import { InlineError } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAddCard } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { groupCardNumber, isValidLuhn, last4Of } from '@/lib/format';

interface Errors {
  cardNumber?: string;
  bank?: string;
  holderName?: string;
}

/**
 * The full PAN is validated in the browser and then discarded: only the last
 * four digits, the bank and the holder name are ever sent. The input is kept
 * out of react-hook-form deliberately so the raw number lives in one piece of
 * local state with a short, obvious lifetime.
 */
export function CardForm({ onDone }: { onDone?: () => void }) {
  const toast = useToast();
  const addCard = useAddCard();

  const [cardNumber, setCardNumber] = useState('');
  const [bank, setBank] = useState<string>(RU_BANKS[0]);
  const [holderName, setHolderName] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const validate = (): Errors => {
    const next: Errors = {};

    const digits = cardNumber.replace(/\D/g, '');
    if (digits.length === 0) {
      next.cardNumber = 'Введите номер карты.';
    } else if (digits.length < 16) {
      next.cardNumber = 'Номер российской карты — от 16 до 19 цифр.';
    } else if (!isValidLuhn(cardNumber)) {
      next.cardNumber = 'Номер не проходит проверку контрольной суммы — сверьте цифры с картой.';
    }

    if (!bank) next.bank = 'Выберите банк-эмитент.';

    const trimmedHolder = holderName.trim();
    if (trimmedHolder.length < 3) {
      next.holderName = 'Введите имя ровно так, как оно напечатано на карте.';
    } else if (!/^[A-Za-zА-Яа-яЁё .'-]+$/.test(trimmedHolder)) {
      next.holderName = 'Только буквы, пробелы, дефисы и апострофы.';
    }

    return next;
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setServerError(null);

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      await addCard.mutateAsync({
        cardNumber: cardNumber.replace(/\D/g, ''),
        last4: last4Of(cardNumber),
        bank,
        holderName: holderName.trim(),
      });

      // Clear the PAN from state the moment it is no longer needed.
      setCardNumber('');
      setHolderName('');
      setErrors({});
      toast.success('Карта добавлена', 'Её проверят при рассмотрении верификации.');
      onDone?.();
    } catch (error) {
      setServerError(toMessage(error, 'Не удалось добавить карту.'));
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate className="space-y-4">
      {serverError ? <InlineError message={serverError} /> : null}

      <TextField
        label="Номер карты"
        required
        monospace
        inputMode="numeric"
        pattern="[0-9 ]*"
        enterKeyHint="next"
        autoComplete="off"
        placeholder="0000 0000 0000 0000"
        value={cardNumber}
        maxLength={23}
        onChange={(event) => setCardNumber(groupCardNumber(event.target.value))}
        error={errors.cardNumber}
        hint="Номер проверяется и хранится в зашифрованном виде для выдачи реквизитов плательщику."
        disabled={addCard.isPending}
      />

      <SelectField
        label="Банк-эмитент"
        required
        value={bank}
        onChange={(event) => setBank(event.target.value)}
        error={errors.bank}
        disabled={addCard.isPending}
      >
        {RU_BANKS.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </SelectField>

      <TextField
        label="Имя держателя карты"
        required
        autoComplete="off"
        autoCapitalize="characters"
        enterKeyHint="done"
        placeholder="IVAN IVANOV"
        value={holderName}
        onChange={(event) => setHolderName(event.target.value)}
        error={errors.holderName}
        hint="Должно совпадать с паспортом. Карты на чужое имя отклоняются."
        disabled={addCard.isPending}
      />

      <div className="flex items-start gap-2.5 rounded-md border border-line bg-sunken px-3 py-2.5">
        <Lock size={13} strokeWidth={1.5} aria-hidden="true" className="mt-0.5 shrink-0 text-fg-muted" />
        <p className="text-xs leading-normal text-fg-secondary">
          Плательщик получает номер только для активного платежа. Срок действия карты и CVV не запрашиваются.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" isLoading={addCard.isPending}>
          {addCard.isPending ? 'Добавляю…' : 'Добавить карту'}
        </Button>
        {onDone ? (
          <Button onClick={onDone} disabled={addCard.isPending}>
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  );
}
